/**
 * Registry-backed lookup for rendering profile labels in the arena UI.
 *
 * Selection state and API payloads travel as `api_name` (the token on the
 * `rank-profile <name>` line — e.g. `fusion`, `fusion_experimental`).
 * The UI always renders the `display_label` from the `.profile` file's
 * `# display:` header (e.g. `Catalog`, `Catalog Experimental`). This hook
 * reads the already-cached `/api/profiles` response via React Query and
 * returns a lookup function. Falls back to the `api_name` itself when the
 * registry hasn't loaded yet or the name is unknown — so nothing ever
 * renders as undefined.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { getProfiles } from "./api";

export function useProfileLabel() {
  const { data } = useQuery({
    queryKey: ["profiles"],
    queryFn: () => getProfiles(),
    staleTime: 5 * 60_000,
  });

  return useMemo(() => {
    const byName = new Map<string, string>();
    for (const p of data?.profiles ?? []) byName.set(p.api_name, p.display_label);
    return (apiName: string | null | undefined): string =>
      apiName ? (byName.get(apiName) ?? apiName) : "";
  }, [data]);
}
